import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  tap,
} from 'rxjs/operators';
import { PersonnelChoisi } from './personnel-choisi';
import { ActivityService } from '../../../services/activity-services';

// ===== NETTOYAGE : sélection du personnel Force-N =====
// Retiré : les propriétés et méthodes de l'ancienne gestion des utilisateurs, que le template n'utilise plus
// (roles, active, update, notification, modales d'activation, pagination serveur, listUsers$…).
// Corrigé : la recherche ne renvoyait plus personne (le « return » du filtre était commenté).

@Component({
  selector: 'app-personnel-forcen',
  templateUrl: './personnel-forcen.component.html',
  styleUrls: ['./personnel-forcen.component.css'],
})
export class PersonnelForcenComponent implements OnInit {
  /** Sélection déjà faite, reprise à l'ouverture */
  @Input() selectionInitiale: PersonnelChoisi[] = [];
  /** On émet les personnes complètes (IUF + prénom + nom) */
  @Output() emitPersonnel = new EventEmitter<PersonnelChoisi[]>();

  showPopup = true;
  personnel: any[] = [];
  personnelChoisi: PersonnelChoisi[] = [];
  ListPersonnalChecked: string[] = [];

  UsersearchCtrl!: FormControl<string | null>;
  filteredUsers$!: Observable<any[]>;

  // Filtre par profil
  profils: string[] = [];
  profilActif = '';
  profilFiltre$ = new BehaviorSubject<string>('');

  // Liste filtrée complète (toutes pages), pour « Tout sélectionner »
  usersFiltres: any[] = [];

  // Pagination locale
  currentPage4Users$ = new BehaviorSubject<number>(1);
  readonly itemsPerPage4Users = 5;
  totalElements = 0;
  totalPages4Users = 1;

  constructor(
    private formBuilder: FormBuilder,
    private activityService: ActivityService,
  ) {}

  ngOnInit(): void {
    // On repart de la sélection existante
    this.personnelChoisi = [...this.selectionInitiale];
    this.ListPersonnalChecked = this.selectionInitiale.map((p) => p.iuf);
    this.UsersearchCtrl = this.formBuilder.control('');
    this.getPersonelForceN();
  }

  private getPersonelForceN(): void {
    this.activityService
      .getAllForceNManagers()
      .pipe(
        tap((personnel: any) => {
          // Le back n'envoie que le personnel Force-N, avec les libellés de profils
          this.personnel = personnel?.content || [];

          // Profils présents, pour les filtres (une personne peut en avoir plusieurs)
          this.profils = [
            ...new Set<string>(
              this.personnel.flatMap((u: any) => u.profils ?? []),
            ),
          ].sort((a, b) => a.localeCompare(b, 'fr'));

          this.initializeUserSearch();
        }),
      )
      .subscribe();
  }

  private initializeUserSearch(): void {
    // Une nouvelle recherche ramène toujours à la page 1
    this.UsersearchCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.currentPage4Users$.next(1));

    this.filteredUsers$ = combineLatest([
      of(this.personnel),
      this.UsersearchCtrl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
      ),
      this.currentPage4Users$.asObservable(),
      this.profilFiltre$.asObservable(),
    ]).pipe(
      map(([users, searchTerm, currentPage, profil]) => {
        const term = (searchTerm ?? '').trim().toLowerCase();
        let filtered = users;

        if (term) {
          filtered = users.filter((u: any) => {
            const matchesBasicFields = ['firstname', 'lastname', 'email'].some(
              (field) =>
                String(u[field] ?? '')
                  .toLowerCase()
                  .includes(term),
            );
            const profileLabel = this.libelleProfils(u).toLowerCase();
            // ===== CORRECTIF : ce return était commenté, la recherche ne trouvait jamais personne =====
            return matchesBasicFields || profileLabel.includes(term);
          });
        }

        // Filtre par profil
        if (profil) {
          filtered = filtered.filter((u: any) => u.profils?.includes(profil));
        }

        // Liste filtrée complète, avant la pagination
        this.usersFiltres = filtered;

        // Pagination locale
        this.totalElements = filtered.length;
        this.totalPages4Users = Math.max(
          1,
          Math.ceil(filtered.length / this.itemsPerPage4Users),
        );
        const startIndex = (currentPage - 1) * this.itemsPerPage4Users;
        return filtered.slice(startIndex, startIndex + this.itemsPerPage4Users);
      }),
    );
  }

  // ---------------------------------------------------------------- sélection
  isSelected(user: any): boolean {
    return this.ListPersonnalChecked.includes(user.iuf);
  }

  /** Clic sur une ligne : coche ou décoche la personne */
  basculer(user: any): void {
    if (this.isSelected(user)) {
      this.ListPersonnalChecked = this.ListPersonnalChecked.filter(
        (p) => p !== user.iuf,
      );
      this.personnelChoisi = this.personnelChoisi.filter(
        (p) => p.iuf !== user.iuf,
      );
    } else {
      this.ListPersonnalChecked.push(user.iuf);
      this.personnelChoisi.push({
        iuf: user.iuf,
        prenom: user.firstname,
        nom: user.lastname,
      });
    }
  }

  toutDeselectionner(): void {
    this.ListPersonnalChecked = [];
    this.personnelChoisi = [];
  }

  /** Croix, Annuler ou clic à côté : ferme sans rien envoyer */
  annuler(): void {
    this.showPopup = false;
  }

  /** Valider : envoie la sélection au formulaire */
  valider(): void {
    this.closeDetails();
  }

  /** Gardée sous ce nom, au cas où un autre composant l'appelle */
  closeDetails(): void {
    this.showPopup = false;
    this.emitPersonnel.emit(this.personnelChoisi);
  }

  // ---------------------------------------------------------------- « Tout sélectionner » (liste filtrée, toutes pages)
  get nbFiltresSelectionnes(): number {
    return this.usersFiltres.filter((u) => this.isSelected(u)).length;
  }

  get tousFiltresSelectionnes(): boolean {
    return (
      this.usersFiltres.length > 0 &&
      this.nbFiltresSelectionnes === this.usersFiltres.length
    );
  }

  get selectionPartielle(): boolean {
    const n = this.nbFiltresSelectionnes;
    return n > 0 && n < this.usersFiltres.length;
  }

  get libelleToutSelectionner(): string {
    if (this.profilActif) {
      return `Tout sélectionner : ${this.profilActif}`;
    }
    return this.UsersearchCtrl?.value
      ? 'Tout sélectionner (résultats de la recherche)'
      : 'Tout sélectionner';
  }

  /** Tout est coché → on décoche le groupe ; sinon → on coche ceux qui manquent */
  basculerTousFiltres(): void {
    if (this.tousFiltresSelectionnes) {
      const iufs = new Set(this.usersFiltres.map((u) => u.iuf));
      this.ListPersonnalChecked = this.ListPersonnalChecked.filter(
        (i) => !iufs.has(i),
      );
      this.personnelChoisi = this.personnelChoisi.filter(
        (p) => !iufs.has(p.iuf),
      );
      return;
    }
    this.usersFiltres
      .filter((u) => !this.isSelected(u))
      .forEach((u) => this.basculer(u));
  }

  // ---------------------------------------------------------------- filtres et affichage
  choisirProfil(profil: string): void {
    this.profilActif = profil;
    this.profilFiltre$.next(profil);
    this.currentPage4Users$.next(1);
  }

  initiales(user: any): string {
    return (
      (user.firstname?.[0] ?? '') + (user.lastname?.[0] ?? '')
    ).toUpperCase();
  }

  /** Libellés venus du back (table roles) */
  libelleProfils(user: any): string {
    return (user.profils ?? []).join(', ') || '—';
  }

  // ---------------------------------------------------------------- pagination
  get pageCourante(): number {
    return this.currentPage4Users$.value;
  }

  get debutPage(): number {
    return this.totalElements === 0
      ? 0
      : (this.pageCourante - 1) * this.itemsPerPage4Users + 1;
  }

  get finPage(): number {
    return Math.min(
      this.pageCourante * this.itemsPerPage4Users,
      this.totalElements,
    );
  }

  pagePrecedente(): void {
    if (this.pageCourante > 1) {
      this.currentPage4Users$.next(this.pageCourante - 1);
    }
  }

  pageSuivante(): void {
    if (this.pageCourante < this.totalPages4Users) {
      this.currentPage4Users$.next(this.pageCourante + 1);
    }
  }
}
// ===== FIN NETTOYAGE =====
